import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { EyeOff, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { useBilling } from "@/context/BillingContext";
import { Paywall } from "@/components/Paywall";
import { SimSkeleton } from "@/components/SimSkeleton";
import { SIM_BY_ID } from "@/data/simulators";
import { trackSimOpen } from "@/lib/track";
import { SUBSCRIPTIONS_ENABLED } from "@/lib/featureFlags";

// Gates a simulator route:
//   1. Global on/off (admin toggle)
//   2. Anonymous users are allowed on FREE sims (no login required to play);
//      SaveSession renders a "Sign in to save" affordance instead of a save
//      button so they only need an account when they want a record.
//   3. Anonymous users hitting a PRO sim see a friendly sign-in paywall
//      (not the upgrade checkout — that requires an account first).
//   4. Signed-in users pass through if they have full access or the sim is free;
//      otherwise the standard Pro paywall renders.
//   When SUBSCRIPTIONS_ENABLED is false every sim is free for everyone — the
//   paywall branches short-circuit to the passthrough.
export function SimGate({ simId, children }) {
  const { simId: paramId } = useParams();
  const id = simId || paramId;
  const navigate = useNavigate();
  const { user, authReady } = useAuth();
  const { isSimVisible, isSimFree, contentReady } = useContent();
  const { entitlement, billingReady } = useBilling();
  const simName = SIM_BY_ID?.[id]?.title;

  const willPassThrough =
    authReady && contentReady && billingReady && isSimVisible(id) && (
      !SUBSCRIPTIONS_ENABLED ||
      (!user ? isSimFree(id) : (entitlement?.access === "full" || isSimFree(id)))
    );

  // Fire a `sim_open` telemetry event exactly once when the gate has decided
  // to render the simulator. Placed here (rather than inside each sim) so
  // every sim gets it "for free" through the routing layer. Runs post-render
  // so it can't block the child mount even if the beacon endpoint is slow.
  useEffect(() => {
    if (willPassThrough && id) trackSimOpen(id);
  }, [willPassThrough, id]);

  if (!authReady || !contentReady || !billingReady) {
    return <SimSkeleton />;
  }

  // globally disabled by admin — but admins can still launch to preview
  // pre-release content. Bypassing here mirrors the tile-level bypass
  // already in place on the section pages.
  if (!isSimVisible(id) && user?.role !== "admin") {
    return (
      <div className="min-h-screen bg-[#F4F7F9] flex items-center justify-center px-4" data-testid="sim-unavailable">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center mx-auto mb-4">
            <EyeOff className="w-6 h-6 text-slate-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Simulator unavailable</h1>
          <p className="text-sm text-slate-600 mt-2">This simulator has been turned off by an administrator.</p>
          <Button onClick={() => navigate("/sections")} className="mt-5 bg-[#0047AB] hover:bg-[#003380] text-white">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to hub
          </Button>
        </div>
      </div>
    );
  }

  const simIsFree = isSimFree(id);
  const hasFullAccess = entitlement?.access === "full";

  // Subscriptions disabled → every sim is free for everyone, whether they're
  // signed in or not. Skip both paywall branches.
  if (!SUBSCRIPTIONS_ENABLED) return children;

  // Anonymous users: free sims are open; Pro sims show a sign-in-focused paywall.
  if (!user) {
    if (simIsFree) return children;
    return <Paywall simName={simName} variant="anon" />;
  }

  // Signed-in users: full access OR a free sim → pass through, otherwise paywall.
  if (hasFullAccess || simIsFree) return children;
  return <Paywall simName={simName} />;
}
